import React, { useState, useEffect, useRef } from 'react';
import { XIcon, ChevronDownIcon, ChevronUpIcon, UserPlusIcon } from 'lucide-react';
import './AddStudentModal.css'; // Continuará importando o mesmo arquivo, mas o conteúdo dele será atualizado

/* --- Estados e Refs --- */
const initialState = {
    nome: '',
    email: '',
    telefone: '',
    matricula: '',
    curso: '',
};

const AddStudentModal = ({ isOpen, onClose, onAddStudent, cursoOptions = [] }) => {
    const [formData, setFormData] = useState(initialState);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    /* --- Efeitos --- */
    useEffect(() => {
        if (!isOpen) {
            setFormData(initialState);
            setIsDropdownOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    /* --- Manipuladores (Handlers) --- */
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleCourseSelect = (curso) => {
        setFormData(prev => ({ ...prev, curso: curso }));
        setIsDropdownOpen(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { nome, email, matricula, curso } = formData;
        if (!nome || !email || !matricula || !curso) {
            alert("Por favor, preencha todos os campos obrigatórios (Nome, Email, Matrícula, Curso).");
            return;
        }
        onAddStudent(formData);
        onClose();
    };

    if (!isOpen) {
        return null;
    }

    /* --- Renderização --- */
    return (
        <div className={`asm-modal-overlay ${isOpen ? 'asm-modal-open' : ''}`} onClick={onClose}>
            <div className="asm-modal-content asm-add-student-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="asm-modal-header">
                    <h2 id="add-student-modal-title" className="asm-modal-title">Adicionar Novo Aluno</h2>
                    <button onClick={onClose} className="asm-modal-close-button" aria-label="Fechar">
                        <XIcon className="asm-icon-xs" />
                    </button>
                </div>
                <p className="asm-modal-description">Preencha as informações do novo aluno</p>

                <form onSubmit={handleSubmit} className="asm-add-student-form">
                    <div className="asm-form-group">
                        <label htmlFor="nome" className="asm-form-label">Nome Completo</label>
                        <input
                            id="nome"
                            className="asm-form-input"
                            placeholder="Digite o nome completo do aluno"
                            value={formData.nome}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="asm-form-group">
                        <label htmlFor="email" className="asm-form-label">Email</label>
                        <input
                            id="email"
                            type="email"
                            className="asm-form-input"
                            placeholder="Digite o email do aluno"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="asm-form-group">
                        <label htmlFor="telefone" className="asm-form-label">Telefone</label>
                        <input
                            id="telefone"
                            className="asm-form-input"
                            placeholder="Digite o telefone do aluno (Opcional)"
                            value={formData.telefone}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="asm-form-group">
                        <label htmlFor="matricula" className="asm-form-label">Matrícula</label>
                        <input
                            id="matricula"
                            className="asm-form-input"
                            placeholder="Digite a matrícula do aluno"
                            value={formData.matricula}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="asm-form-group">
                        <label htmlFor="curso" className="asm-form-label">Curso</label>
                        <div className="asm-dropdown-container" ref={dropdownRef} style={{ width: '100%' }}>
                            <button
                                type="button"
                                id="curso"
                                className="asm-form-select-button"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                aria-haspopup="listbox"
                                aria-expanded={isDropdownOpen}
                            >
                                <span className="asm-form-select-button-text">
                                    {formData.curso || "Selecione o curso"}
                                </span>
                                {isDropdownOpen ?
                                    <ChevronUpIcon className="asm-form-select-button-icon" /> :
                                    <ChevronDownIcon className="asm-form-select-button-icon" />
                                }
                            </button>
                            {isDropdownOpen && (
                                <ul className="asm-dropdown-options-list" role="listbox">
                                    {(cursoOptions || []).map((cursoOpt) => (
                                        <li
                                            key={cursoOpt}
                                            className="asm-dropdown-option-item"
                                            onClick={() => handleCourseSelect(cursoOpt)}
                                            role="option"
                                            aria-selected={formData.curso === cursoOpt}
                                        >
                                            {cursoOpt}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="asm-modal-actions">
                        <button type="button" onClick={onClose} className="asm-button asm-button-secondary">
                            Cancelar
                        </button>
                        <button type="submit" className="asm-button asm-button-primary">
                            <UserPlusIcon className="asm-icon-sm" style={{ marginRight: '0.5rem' }} />
                            Adicionar Aluno
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddStudentModal;